import { createFileRoute, Outlet, useRouterState } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";
import { SM_PLANS, planPriceArs } from "@/lib/pricing-catalog";
import { ContratarModal } from "@/components/onboarding/ContratarModal";
import { OnboardingLeadForm } from "@/components/onboarding/OnboardingLeadForm";
import type { SaasBankTransfer, SaasBankTransferUsd } from "@/lib/saas-billing-config";
import { onboardingCustomerCopyForSession } from "@/lib/onboarding-customer-copy";
import { publicSitePath } from "@/lib/public-site";
import { nicheFromSearchParams } from "@/lib/onboarding-canonical";

const CONTRATAR_TITLE = "Contratar Structura Market";
const CONTRATAR_DESCRIPTION = "Activá tu tienda online en minutos.";

export const Route = createFileRoute("/contratar")({
  component: ContratarPage,
  head: () => ({
    meta: [
      { title: CONTRATAR_TITLE },
      { name: "description", content: CONTRATAR_DESCRIPTION },
      { property: "og:title", content: CONTRATAR_TITLE },
      { property: "og:description", content: CONTRATAR_DESCRIPTION },
      { property: "og:url", content: publicSitePath("/contratar") },
    ],
    links: [{ rel: "canonical", href: publicSitePath("/contratar") }],
  }),
});

/**
 * Único onboarding self-service: ContratarModal (Stitch dark).
 * El plan se elige en la landing; acá no hay picker ni shell light.
 */
const PUBLIC_PLANS = SM_PLANS.filter((p) => p.id !== "free_trial" && p.priceArs != null);
const DEFAULT_ANNUAL_DISCOUNT = 25;

function backUrl(): string {
  // Volver al origen del CTA (landingMarket u otra página FM). Nunca forzar redirect de food → landing.
  if (typeof document !== "undefined" && document.referrer) {
    try {
      const ref = new URL(document.referrer);
      if (ref.pathname !== "/contratar") {
        return ref.href;
      }
    } catch {
      /* ignore */
    }
  }
  return "/landing";
}

function ContratarPage() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [planId, setPlanId] = useState("starter");
  const [billingMode, setBillingMode] = useState<"monthly_oneshot" | "annual_oneshot">("monthly_oneshot");
  const [urlSessionId, setUrlSessionId] = useState<string | null>(null);
  const [ualaReturn, setUalaReturn] = useState<"success" | "failed" | null>(null);
  const [activationMessage, setActivationMessage] = useState<string | null>(null);
  const [bankTransferArs, setBankTransferArs] = useState<SaasBankTransfer | null>(null);
  const [annualDiscountPct, setAnnualDiscountPct] = useState(DEFAULT_ANNUAL_DISCOUNT);
  const [selfServiceEnabled, setSelfServiceEnabled] = useState<boolean | null>(null);
  const [presetProfile, setPresetProfile] = useState<string | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(true);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const sid = q.get("session");
    if (sid) setUrlSessionId(sid);
    const uala = q.get("uala");
    if (uala === "success") setUalaReturn("success");
    else if (uala === "failed") setUalaReturn("failed");
    const niche = nicheFromSearchParams(q);
    if (niche) setPresetProfile(niche);
    const planQ = q.get("plan");
    if (planQ && PUBLIC_PLANS.some((p) => p.id === planQ)) {
      setPlanId(planQ);
    }
    const billingQ = q.get("billing");
    if (billingQ === "monthly_oneshot" || billingQ === "annual_oneshot") {
      setBillingMode(billingQ);
    }
  }, []);

  useEffect(() => {
    if (!urlSessionId) return;
    let cancelled = false;
    const tick = async () => {
      const res = await fetch(`/api/self-service/session?id=${encodeURIComponent(urlSessionId)}`);
      if (!res.ok || cancelled) return;
      const data = (await res.json()) as { status?: string; paymentConfirmedAt?: string | null };
      if (!data.status) return;
      setActivationMessage(
        onboardingCustomerCopyForSession({
          status: data.status,
          paymentConfirmedAt: data.paymentConfirmedAt ?? null,
        }),
      );
    };
    void tick();
    const id = window.setInterval(() => {
      void tick();
    }, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [urlSessionId]);

  useEffect(() => {
    void fetch("/api/pricing")
      .then((r) => r.json())
      .then(
        (d: {
          billingBankTransferArs?: SaasBankTransfer | null;
          billingBankTransferUsd?: SaasBankTransferUsd | null;
          billingAnnualDiscountPct?: number;
          selfServiceEnabled?: boolean;
        }) => {
          setBankTransferArs(d.billingBankTransferArs ?? null);
          if (typeof d.billingAnnualDiscountPct === "number") setAnnualDiscountPct(d.billingAnnualDiscountPct);
          setSelfServiceEnabled(d.selfServiceEnabled !== false);
        },
      )
      .catch(() => {
        setSelfServiceEnabled(false);
      });
  }, []);

  const plan = PUBLIC_PLANS.find((p) => p.id === planId) ?? PUBLIC_PLANS[0];
  const priceMonthly = planPriceArs(plan);
  const showCheckoutReturn = Boolean(urlSessionId) && ualaReturn !== "failed";

  const leaveOnboarding = () => {
    setModalOpen(false);
    window.location.assign(backUrl());
  };

  if (pathname !== "/contratar") {
    return <Outlet />;
  }

  if (selfServiceEnabled === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a]">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (selfServiceEnabled === false) {
    return (
      <div className="min-h-screen bg-background">
        <main className="mx-auto max-w-2xl px-4 py-10">
          <OnboardingLeadForm defaultRubro={presetProfile ?? "market"} />
        </main>
      </div>
    );
  }

  if (showCheckoutReturn) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a] px-4">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center text-foreground">
          <h1 className="text-xl font-bold">Estamos activando tu cuenta</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            {activationMessage ??
              (ualaReturn === "success"
                ? "El pago se registró. En unos segundos vas a poder ingresar."
                : "Si confirmaste el pago, tu cuenta se activa en breve.")}
          </p>
          <a href="/acceso/comercio" className="mt-6 inline-block text-sm font-semibold text-primary hover:underline">
            Ir a ingresar
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      {ualaReturn === "failed" && (
        <div className="mx-auto max-w-lg px-4 pt-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              El pago no se completó. Tu tienda no está activa. Podés reintentar desde el checkout.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {plan && (
        <ContratarModal
          open={modalOpen}
          onOpenChange={(v) => {
            if (!v) leaveOnboarding();
            else setModalOpen(true);
          }}
          planId={plan.id}
          planName={plan.name}
          priceMonthly={priceMonthly}
          billingMode={billingMode}
          annualDiscountPct={annualDiscountPct}
          bankTransferArs={bankTransferArs}
          mode="payment"
          businessProfile={presetProfile}
        />
      )}
    </div>
  );
}
